import { useState } from "react";

import { useSendTestEmail } from "../../../hooks/mutations/admin/emailTemplates";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";
import { ModalBodyText } from "./AdminSettings.styles";

/** Enough to catch a typo; the server is the real validator. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ISendTestEmailModalProps {
  subject: string;
  html: string;
  wardName: string;
  onClose: () => void;
}

/**
 * Send-a-test-email confirmation (EN-5162).
 *
 * This is the one outbound action on the /admin pages: it puts a real email
 * in a real inbox. The ticket is explicit that it must never fire on page
 * load or a select change, so the send lives behind this modal, needs an
 * address typed in, and the request is only made by the confirm button —
 * `useSendTestEmail` has no auto-run and no other caller.
 *
 * Retool sent it straight off a button beside the address field, with the
 * subject and body taken from whatever was on screen and no confirmation.
 */
export function SendTestEmailModal({
  subject,
  html,
  wardName,
  onClose,
}: ISendTestEmailModalProps) {
  const { showToast } = useToast();
  const { sendTestEmail, isSendingTestEmail } = useSendTestEmail();

  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  const emailError = (() => {
    if (!email.trim()) return "An address is required.";
    if (!EMAIL_PATTERN.test(email.trim())) return "That isn't a valid address.";
    return undefined;
  })();
  const shownError = touched ? emailError : undefined;

  const handleSend = async () => {
    if (emailError) return;

    try {
      await sendTestEmail({
        email: email.trim(),
        subject,
        htmlTemplate: html,
      });
      showToast("The email test was sent successfully.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error sending the test email."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Send a test email</ModalTitle>

      <ModalForm>
        <ModalBodyText>
          Sends the template as it is currently edited — subject{" "}
          <strong>{subject || "(none)"}</strong>, from{" "}
          <strong>{wardName}</strong>. Unsaved changes are included; this does
          not save the template.
        </ModalBodyText>

        <WarningNotice>
          This sends a real email. Use your own address — never a
          patient&apos;s.
        </WarningNotice>

        <FilterGroup>
          <FilterLabel htmlFor="test-email-address">
            Recipient address
          </FilterLabel>
          <FilterInput
            id="test-email-address"
            type="email"
            autoComplete="off"
            value={email}
            disabled={isSendingTestEmail}
            onBlur={() => setTouched(true)}
            onChange={(e) => {
              setTouched(true);
              setEmail(e.target.value);
            }}
          />
          {shownError && <FieldError>{shownError}</FieldError>}
          <FieldHint>The address the test message will be sent to.</FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSendingTestEmail}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSendingTestEmail || Boolean(emailError)}
            onClick={handleSend}
          >
            {isSendingTestEmail ? "Sending..." : "Send test email"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
